import React from "react";

import { useState, useMemo, useLayoutEffect, useCallback } from "react";
import { useMutation, gql } from "@apollo/client";
import authSessionContext from "./context";
import * as authStore from "../../@lib/services/storage/auth";

export type AuthSessionProviderProps = {
  children?: React.ReactNode;
};

const loginUserMutationDocument = gql`
  mutation UserLogin($email: String!, $password: String!) {
    userLogin(email: $email, password: $password) {
      credentials {
        accessToken
        expiry
        uid
        client
      }
      authenticatable {
        email
        firstName
        lastName
        lang
        profile {
          name
        }
      }
    }
  }
`;

const AuthSessionProvider: React.FC<AuthSessionProviderProps> = (props) => {
  const [user, setUser] = useState<any | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [passwordToken, setPasswordToken] = useState<any>("");

  const [loginUser] = useMutation(loginUserMutationDocument);

  async function handleResetPasswordToken(token: string) {
    await Promise.all([authStore.setPasswordResetToken(token)]);
    setPasswordToken(token);
  }

  /**
   * handleLoginUser()
   */
  const handleLoginUser = useCallback(
    async (email: string, password: string) => {
      const {
        data: {
          userLogin: {
            credentials: { accessToken, expiry, uid, client },
            authenticatable: { firstName, lastName, lang, profile },
          },
        },
      } = await loginUser({ variables: { email, password } });

      await Promise.all([
        authStore.setAccessToken(accessToken),
        authStore.setTokenExpiry(expiry * 1000),
        authStore.setUser({
          email: uid,
          id: uid,
          accessToken: accessToken,
          client: client,
          firstName: firstName,
          lastName: lastName,
          lang: lang,
          expiry: expiry,
          profile: {
            name: profile?.name?.toLowerCase(),
            __typename: profile?.__typename,
          },
        }),
      ]);

      setUser({
        email: uid,
        id: uid,
        accessToken: accessToken,
        client: client,
        firstName: firstName,
        lastName: lastName,
        lang: lang,
        expiry: expiry,
        profile: {
          name: profile?.name?.toLowerCase(),
          __typename: profile?.__typename,
        },
      });
    },
    [loginUser]
  );

  /**
   * logoutUser()
   */
  const logoutUser = useCallback(async () => {
    await authStore.clear();
    setUser(null);
  }, []);

  /**
   * authSession
   */
  const authSession = useMemo(
    () => ({
      user,
      passwordToken,
      loginUser: handleLoginUser,
      logoutUser,
      resetPasswordToken: handleResetPasswordToken,
    }),
    [user, handleLoginUser, logoutUser, handleResetPasswordToken, passwordToken]
  );

  // Retrieve authentication state.
  useLayoutEffect(() => {
    (async () => {
      try {
        setUser(await authStore.getUser());
        setPasswordToken(await authStore.getPasswordResetToken());
      } catch (err) {
        console.error(err);
      } finally {
        setInitializing(false);
      }
    })();
  }, []);

  return (
    <authSessionContext.Provider value={authSession}>
      {!initializing && props.children}
    </authSessionContext.Provider>
  );
};

export default AuthSessionProvider;
